import React, { useId } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import type { KpiMetric, DailySeries } from '../../services/api';

type Accent = 'blue' | 'violet' | 'emerald' | 'amber';

interface KpiCardProps {
  title: string;
  metric: KpiMetric;
  format: 'currency' | 'number' | 'percent';
  sparkData?: DailySeries[];
  sparkKey?: string;
  invertColor?: boolean; // true for refund rate (lower = better)
  icon: React.ReactNode;
  accent?: Accent;
}

const ACCENT_CHIP: Record<Accent, string> = {
  blue: 'bg-blue-50 text-blue-600',
  violet: 'bg-violet-50 text-violet-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
};

const formatValue = (value: number, format: string) => {
  if (format === 'currency') {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  }
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'number') {
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return value.toLocaleString();
  }
  return String(value);
};

export const KpiCard: React.FC<KpiCardProps> = ({
  title, metric, format, sparkData, sparkKey = 'netRevenue', invertColor = false, icon, accent = 'blue'
}) => {
  const gradientId = useId();
  const isPositive = invertColor
    ? (metric.deltaPct !== null && metric.deltaPct < 0)
    : (metric.deltaPct !== null && metric.deltaPct > 0);
  const isNegative = invertColor
    ? (metric.deltaPct !== null && metric.deltaPct > 0)
    : (metric.deltaPct !== null && metric.deltaPct < 0);

  const sparkColor = isPositive ? '#059669' : isNegative ? '#dc2626' : '#2563eb';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-500">{title}</span>
        <span className={`rounded-lg p-1.5 ${ACCENT_CHIP[accent]}`}>{icon}</span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="text-3xl font-bold text-gray-900 tracking-tight tabular-nums">
            {formatValue(metric.current, format)}
          </div>
          {metric.deltaPct !== null ? (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
                isPositive ? 'bg-emerald-50 text-emerald-700'
                : isNegative ? 'bg-red-50 text-red-700'
                : 'bg-gray-100 text-gray-500'
              }`}>
                {isPositive ? <ArrowUpRight size={12} /> : isNegative ? <ArrowDownRight size={12} /> : <Minus size={12} />}
                {Math.abs(metric.deltaPct).toFixed(1)}%
              </span>
              <span className="text-xs text-gray-400">vs last year</span>
            </div>
          ) : (
            <div className="text-xs text-gray-400 mt-1.5">No comparison data</div>
          )}
        </div>
        {sparkData && sparkData.length > 1 && (
          <div className="w-24 h-12 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={sparkColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey={sparkKey}
                  stroke={sparkColor}
                  fill={`url(#${gradientId})`}
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
